import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { payApi } from "../../../api/pay";
import { adminApi } from "../../../api/resources";
import { Button, TextInput } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";

const sel = "h-11 rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";
const EMPTY = { employee_id: "", effective_from: "", ot_exempt: false, ot_exempt_reason: "", alt_schedule_4x10: false, agreement_date: "", agreement_note: "" };

export function ProfilesTab() {
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => Promise.all([payApi.profiles(), adminApi.employees()]), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  if (!data) return <LoadState error={error} loading={!error} testId="profiles-load" />;
  const [profiles, employees] = data;
  const name = (id: string) => employees.find((e) => e.id === id)?.full_name ?? id;
  const save = async () => {
    setBusy(true);
    try {
      await payApi.createProfile({ ...f, ot_exempt_reason: f.ot_exempt_reason || null, agreement_date: f.agreement_date || null, agreement_note: f.agreement_note || null });
      toast.success("Pay profile saved");
      setF(EMPTY);
      setNonce((n) => n + 1);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="pay-profiles-tab">
      <p className="mb-4 max-w-3xl text-xs text-zinc-400">Effective-dated per-employee flags. Both flags require payroll advisor review. OT-exempt needs a reason; a 4×10 schedule (daily threshold 10h) needs an agreement date and note.</p>
      <div className="mb-6 grid gap-2 rounded-sm border border-white/10 p-4 sm:grid-cols-4" data-testid="profile-form">
        <select className={sel} value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="profile-employee-select"><option value="">Employee…</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}</select>
        <TextInput type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} data-testid="profile-effective-input" />
        <label className="flex items-center gap-2 text-sm text-zinc-200"><input type="checkbox" checked={f.ot_exempt} onChange={(e) => setF({ ...f, ot_exempt: e.target.checked })} className="h-4 w-4 accent-[#ff1d00]" data-testid="profile-ot-exempt" />OT exempt</label>
        <label className="flex items-center gap-2 text-sm text-zinc-200"><input type="checkbox" checked={f.alt_schedule_4x10} onChange={(e) => setF({ ...f, alt_schedule_4x10: e.target.checked })} className="h-4 w-4 accent-[#ff1d00]" data-testid="profile-4x10" />4×10 schedule</label>
        {f.ot_exempt && <TextInput placeholder="Exempt reason" value={f.ot_exempt_reason} onChange={(e) => setF({ ...f, ot_exempt_reason: e.target.value })} data-testid="profile-exempt-reason" />}
        {f.alt_schedule_4x10 && <TextInput type="date" value={f.agreement_date} onChange={(e) => setF({ ...f, agreement_date: e.target.value })} data-testid="profile-agreement-date" />}
        {f.alt_schedule_4x10 && <TextInput placeholder="Agreement note" value={f.agreement_note} onChange={(e) => setF({ ...f, agreement_note: e.target.value })} data-testid="profile-agreement-note" />}
        <Button onClick={save} loading={busy} disabled={!f.employee_id || !f.effective_from} data-testid="profile-save-btn">Save profile</Button>
      </div>
      <DataTable headers={["Employee", "Effective", "OT exempt", "4×10", "Notes"]} testId="profiles-table" empty={!profiles.length}>
        {profiles.map((p) => (
          <tr key={p.id} className="border-t border-white/5" data-testid={`profile-row-${p.id}`}>
            <td className="px-5 py-3 text-white">{name(p.employee_id)}</td>
            <td className="px-5 py-3 font-mono text-xs text-zinc-400">{p.effective_from}</td>
            <td className="px-5 py-3 text-zinc-300">{p.ot_exempt ? "Yes" : "No"}</td>
            <td className="px-5 py-3 text-zinc-300">{p.alt_schedule_4x10 ? `Yes (${p.agreement_date})` : "No"}</td>
            <td className="px-5 py-3 text-xs text-zinc-500">{p.ot_exempt_reason ?? p.agreement_note ?? "—"} <span className="text-amber-300">· advisor review</span></td>
          </tr>
        ))}
      </DataTable>
    </section>
  );
}
